import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Plus, Edit2, Trash2, Upload, X } from "lucide-react";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", cover_image: "",
  tags: [], author: "Wünberg Editör", published: true,
  seo_title: "", seo_description: "",
};

function slugify(s) {
  return s.toLowerCase()
    .replace(/ı/g,"i").replace(/ğ/g,"g").replace(/ü/g,"u").replace(/ş/g,"s").replace(/ö/g,"o").replace(/ç/g,"c")
    .replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"");
}

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/blog", { params: { published_only: false } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEdit(null); setOpen(true); };
  const openEdit = (p) => { setEdit(p); setForm({ ...EMPTY, ...p, tags: p.tags || [] }); setOpen(true); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF("cover_image", `${API_BASE.replace("/api","")}${data.url}`);
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
    };
    try {
      if (edit) await api.put(`/blog/${edit.id}`, payload);
      else await api.post("/blog", payload);
      toast.success(edit ? "Yazı güncellendi" : "Yazı eklendi");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
    try {
      await api.delete(`/blog/${p.id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="wb-overline">Yönetim</div>
          <h1 className="wb-h2 mt-2">Blog</h1>
        </div>
        <button onClick={openNew} className="wb-btn-primary" data-testid="admin-blog-new">
          <Plus size={16} /> Yeni Yazı
        </button>
      </div>

      <div className="wb-card mt-8 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-[#a0aabf] text-xs uppercase tracking-widest">
              <th className="p-4">Başlık</th>
              <th className="p-4">Yazar</th>
              <th className="p-4">Durum</th>
              <th className="p-4">Tarih</th>
              <th className="p-4 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-[#a0aabf]">Henüz yazı eklenmedi.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`row-blog-${p.slug}`}>
                <td className="p-4">
                  <div>{p.title}</div>
                  <div className="text-xs text-[#a0aabf]">/{p.slug}</div>
                </td>
                <td className="p-4 text-[#a0aabf]">{p.author}</td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 ${p.published ? "bg-[#d4af37]/10 text-[#d4af37]" : "bg-white/5 text-[#a0aabf]"}`}>
                    {p.published ? "Yayında" : "Taslak"}
                  </span>
                </td>
                <td className="p-4 text-[#a0aabf] text-xs">{new Date(p.created_at).toLocaleDateString("tr-TR")}</td>
                <td className="p-4 text-right space-x-2">
                  <button onClick={() => openEdit(p)} className="p-2 border border-white/10 hover:border-[#d4af37] hover:text-[#d4af37]"><Edit2 size={14} /></button>
                  <button onClick={() => remove(p)} className="p-2 border border-white/10 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-[#081b3d] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#081b3d]">
              <h2 className="font-serif text-xl">{edit ? "Yazıyı Düzenle" : "Yeni Yazı"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className="wb-input" placeholder="Başlık" required value={form.title} onChange={(e) => { setF("title", e.target.value); if (!edit) setF("slug", slugify(e.target.value)); }} data-testid="blog-form-title" />
                <input className="wb-input" placeholder="slug" required value={form.slug} onChange={(e) => setF("slug", e.target.value)} />
              </div>
              <input className="wb-input" placeholder="Özet" required value={form.excerpt} onChange={(e) => setF("excerpt", e.target.value)} data-testid="blog-form-excerpt" />
              <textarea className="wb-input min-h-[260px]" placeholder="İçerik (Markdown desteklenir)" required value={form.content} onChange={(e) => setF("content", e.target.value)} data-testid="blog-form-content" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className="wb-input" placeholder="Yazar" value={form.author} onChange={(e) => setF("author", e.target.value)} />
                <input className="wb-input" placeholder="Etiketler (virgülle)" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setF("tags", e.target.value)} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-[#a0aabf]">Kapak Görseli</label>
                <div className="mt-2 flex items-center gap-3">
                  {form.cover_image && <img src={form.cover_image} alt="" className="w-20 h-20 object-cover border border-white/10" />}
                  <label className="wb-btn-secondary cursor-pointer !py-2 !px-4 text-xs">
                    <Upload size={14} /> {uploading ? "Yükleniyor..." : "Yükle"}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="blog-upload" />
                  </label>
                  {form.cover_image && <button type="button" className="text-xs text-red-400" onClick={() => setF("cover_image", "")}>Kaldır</button>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className="wb-input" placeholder="SEO Başlık" value={form.seo_title || ""} onChange={(e) => setF("seo_title", e.target.value)} />
                <input className="wb-input" placeholder="SEO Açıklama" value={form.seo_description || ""} onChange={(e) => setF("seo_description", e.target.value)} />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={(e) => setF("published", e.target.checked)} />
                Yayında
              </label>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setOpen(false)} className="wb-btn-secondary">İptal</button>
                <button type="submit" className="wb-btn-primary" data-testid="blog-form-submit">{edit ? "Güncelle" : "Kaydet"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
